import React, { useEffect, useState } from "react";

import { BellRing, ChartLine, Radar, Sparkles, Telescope } from "lucide-react";
import { useLocation } from "react-router-dom";

import TabsComponent from "../../../../../Components/Tab";
import BusinessKpiBriefingsPlaceholder from "../BusinessKpiBriefingsPlaceholder";

export type BriefingWorkspaceTab = "daily-digest" | "temp-public" | "kpi";
export type BriefingParentTab = "attention" | "birds-eye" | "signals";

export interface BriefingWorkspaceProps {
  /**
   * Right side of the Bird's Eye / Attention / Signals row — visible for every
   * parent tab (portfolio scope, briefing date, etc.).
   */
  parentHeaderRight?: React.ReactNode;
  /** Optional right side on the Attention sub-tab row only. */
  headerRight?: React.ReactNode;
  /** Right side of Attention sub-tab row when Enterprise KPIs is active. */
  kpiHeaderRight?: React.ReactNode;
  /**
   * Temp Public Data — xfactor-powered public data matrix.
   */
  tempPublicDataContent?: React.ReactNode;
  /** Business KPI tab content */
  businessKpiContent?: React.ReactNode;
  /** Optional content for Bird's Eye parent tab. */
  birdsEyeContent?: React.ReactNode;
  /** Optional content for Signals parent tab. */
  signalsContent?: React.ReactNode;
  /**
   * Controlled top tabs (Bird's Eye / Attention / Signals). Use with `onBriefingParentTabChange`
   * so the active tab survives switching Any Portfolio ↔ My Portfolio (separate tab components).
   */
  briefingParentTab?: BriefingParentTab;
  onBriefingParentTabChange?: (tab: BriefingParentTab) => void;
  /**
   * Controlled Attention sub-tab (Temp Public Data / Enterprise KPIs). Use with
   * `onBriefingSubTabChange` so the accounts sidebar can react to Enterprise KPIs.
   */
  briefingSubTab?: BriefingWorkspaceTab;
  onBriefingSubTabChange?: (tab: BriefingWorkspaceTab) => void;
}

const SUB_TABS = [
  {
    id: "daily-digest" as const,
    label: "Daily Digest",
    icon: <BellRing size={14} strokeWidth={1.75} />,
    disabled: true,
  },
  {
    id: "temp-public" as const,
    label: "Business Health",
    icon: <Sparkles size={14} strokeWidth={1.75} />,
  },
  {
    id: "kpi" as const,
    label: "Account Health",
    icon: <ChartLine size={14} strokeWidth={1.75} />,
  },
];

const PARENT_TABS = [
  {
    id: "birds-eye" as const,
    label: "Bird's Eye",
    icon: <Telescope size={14} strokeWidth={1.75} />,
  },
  {
    id: "attention" as const,
    label: "Attention",
    icon: <Radar size={14} strokeWidth={1.75} />,
  },
  {
    id: "signals" as const,
    label: "Signals",
    icon: <BellRing size={14} strokeWidth={1.75} />,
    disabled: true,
  },
];

const BriefingWorkspace: React.FC<BriefingWorkspaceProps> = ({
  parentHeaderRight,
  headerRight,
  kpiHeaderRight,
  tempPublicDataContent,
  businessKpiContent,
  birdsEyeContent,
  signalsContent,
  briefingParentTab: briefingParentTabProp,
  onBriefingParentTabChange,
  briefingSubTab: briefingSubTabProp,
  onBriefingSubTabChange,
}) => {
  const location = useLocation();
  const [internalParentTab, setInternalParentTab] =
    useState<BriefingParentTab>("attention");
  const parentTab =
    briefingParentTabProp !== undefined
      ? briefingParentTabProp
      : internalParentTab;

  const handleParentTabChange = (id: BriefingParentTab) => {
    onBriefingParentTabChange?.(id);
    if (briefingParentTabProp === undefined) {
      setInternalParentTab(id);
    }
  };

  const [internalSubTab, setInternalSubTab] =
    useState<BriefingWorkspaceTab>("temp-public");
  const subTab =
    briefingSubTabProp !== undefined ? briefingSubTabProp : internalSubTab;

  const handleSubTabChange = (id: BriefingWorkspaceTab) => {
    onBriefingSubTabChange?.(id);
    if (briefingSubTabProp === undefined) {
      setInternalSubTab(id);
    }
  };

  const onBriefingSubTabChangeRef = React.useRef(onBriefingSubTabChange);
  const onBriefingParentTabChangeRef = React.useRef(onBriefingParentTabChange);
  onBriefingSubTabChangeRef.current = onBriefingSubTabChange;
  onBriefingParentTabChangeRef.current = onBriefingParentTabChange;

  // Apply briefingTab from URL only when the URL changes (deep-link / AO Channel).
  // Must not depend on controlled sub-tab state — otherwise landing with
  // ?briefingTab=kpi blocks switching away from KPIs.
  // Legacy ?briefingTab=public maps to Temp Public Data (Public Data tab removed).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = (params.get("briefingTab") || "").toLowerCase();
    const setSub = (next: BriefingWorkspaceTab) => {
      onBriefingSubTabChangeRef.current?.(next);
      if (briefingSubTabProp === undefined) {
        setInternalSubTab(next);
      }
    };
    if (tab === "kpi" || tab === "enterprise-kpis" || tab === "enterprise") {
      setSub("kpi");
      onBriefingParentTabChangeRef.current?.("attention");
      if (briefingParentTabProp === undefined) {
        setInternalParentTab("attention");
      }
      return;
    }
    if (tab === "daily-digest" || tab === "digest") {
      setSub("temp-public");
      return;
    }
    if (tab === "temp-public" || tab === "temp" || tab === "public") {
      setSub("temp-public");
      onBriefingParentTabChangeRef.current?.("attention");
      if (briefingParentTabProp === undefined) {
        setInternalParentTab("attention");
      }
      return;
    }
    if (tab === "signals") {
      onBriefingParentTabChangeRef.current?.("attention");
      if (briefingParentTabProp === undefined) {
        setInternalParentTab("attention");
      }
    }
  }, [location.search]);

  return (
    <div className="briefing-workspace flex h-full min-h-0 flex-col overflow-hidden rounded-t-lg">
      {/* Parent tabs on top; child tabs show only inside Attention. */}
      <div className="shrink-0 bg-white px-4">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 py-2.5">
          <div className="min-w-0 w-fit shrink-0">
            <TabsComponent
              tabs={PARENT_TABS}
              activeTab={parentTab}
              onTabChange={id => handleParentTabChange(id as BriefingParentTab)}
              variant="secondary-segmented"
              segmentedLayout="fit"
              className="mb-0 whitespace-nowrap rounded-full"
            />
          </div>
          {parentHeaderRight ? (
            <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-end gap-3 md:flex-nowrap md:gap-4">
              {parentHeaderRight}
            </div>
          ) : null}
        </div>
        {parentTab === "attention" && (
          <div className="flex min-w-0 flex-nowrap items-center justify-between gap-2 border-b border-gray-200 sm:gap-4">
            <div className="min-w-0 shrink overflow-x-auto">
              <TabsComponent
                tabs={SUB_TABS}
                activeTab={subTab}
                onTabChange={id =>
                  handleSubTabChange(id as BriefingWorkspaceTab)
                }
                variant="tab-underline"
                className="mb-0 whitespace-nowrap"
              />
            </div>
            {subTab === "kpi" && kpiHeaderRight ? (
              <div className="flex shrink-0 flex-nowrap items-center justify-end gap-2 overflow-x-auto pl-2">
                {kpiHeaderRight}
              </div>
            ) : headerRight && subTab === "temp-public" ? (
              <div className="flex min-w-0 shrink-0 flex-nowrap items-center justify-end gap-3 md:gap-4">
                {headerRight}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
        {parentTab === "attention" ? (
          subTab === "daily-digest" ? (
            <div className="p-6 text-sm text-gray-500">
              Daily Digest coming soon.
            </div>
          ) : subTab === "temp-public" ? (
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
              {tempPublicDataContent}
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
              {businessKpiContent ?? <BusinessKpiBriefingsPlaceholder />}
            </div>
          )
        ) : parentTab === "birds-eye" ? (
          <div className="flex h-full min-h-0 flex-col">
            {birdsEyeContent ?? null}
          </div>
        ) : (
          (signalsContent ?? (
            <div className="p-6 text-sm text-gray-500">
              Signals coming soon.
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BriefingWorkspace;
